import { t } from "@/i18n-js/instance";
import { useDeleteSpaceModal } from "@/react/components/Modals/DeleteSpaceModal";
import { useLeaveSpaceConfirmationModal } from "@/react/components/Modals/LeaveSpaceConfirmationModal";
import { canInviteMembers, isCourseSpace } from "@/react/helpers/spaceHelpers";
import { useMobileWebFlags } from "@circle-react/hooks/useMobileWebFlags";
import type { Space } from "@circle-react/types";
import { Icon } from "@circle-react-shared/Icon";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { Typography } from "@circle-react-uikit/Typography";
import { useNotificationBell } from "../../SpaceHeaderActions/NotificationBellDropdown/useNotificationBell";
import { ChatExportSpaceOptions } from "./ChatExportSpaceOptions";
import { DeleteSpaceOption } from "./DeleteSpaceOption";
import { InviteMemberSpaceOptions } from "./InviteMemberSpaceOptions";
import { LeaveSpaceOption } from "./LeaveSpaceOption";
import { spaceIconsV3 } from "./useSpaceOptionsIcons";

export interface LeaveAndDeleteSpaceOptionsProps {
  isAdmin: boolean;
  onChatExport?: () => void;
  onInviteMember?: () => void;
  space: Space;
}

export const LeaveAndDeleteSpaceOptions = ({
  space,
  isAdmin,
  onChatExport,
  onInviteMember,
}: LeaveAndDeleteSpaceOptionsProps) => {
  const { isMobileWebNewSpaceCustomizationUIEnabled } = useMobileWebFlags();
  const deleteSpaceModal = useDeleteSpaceModal();
  const redirectToRoot = () => (window.location.href = "/");
  const leaveSpaceConfirmationModal = useLeaveSpaceConfirmationModal();
  const { leaveSpaceIcon, deleteSpaceIcon } = spaceIconsV3;
  const isSpaceCourseSpace = isCourseSpace(space);

  const shouldShowInviteMember = canInviteMembers(space) && !!onInviteMember;
  const shouldShowChatExport = isAdmin && !!onChatExport;

  const shouldShowLeaveSpace = !!space.policies?.can_leave_space;

  const { shouldShowNotificationsSection } = useNotificationBell({
    space,
  });

  const shouldShowV3Separator =
    isAdmin ||
    shouldShowInviteMember ||
    shouldShowChatExport ||
    (shouldShowNotificationsSection && !isSpaceCourseSpace);

  if (isMobileWebNewSpaceCustomizationUIEnabled) {
    return (
      <>
        {shouldShowChatExport && (
          <button
            type="submit"
            className="flex w-full items-center justify-between px-5 py-2.5"
            onClick={onChatExport}
          >
            <div className="flex items-center gap-4">
              <Icon type="20-download" size={20} className="text-default" />
              <Typography.LabelSm color="text-light" weight="medium">
                {t("space_header.chat_export")}
              </Typography.LabelSm>
            </div>
            <Icon type="16-chevron-right" size={16} className="text-default" />
          </button>
        )}
        {shouldShowLeaveSpace && (
          <button
            type="submit"
            className="flex w-full items-center gap-4 px-5 py-2"
            onClick={() => leaveSpaceConfirmationModal.show()}
          >
            <Icon type={leaveSpaceIcon} className="text-v2-danger" size={20} />
            <Typography.LabelSm color="text-v2-danger">
              {t("space_header.leave_space")}
            </Typography.LabelSm>
          </button>
        )}
        {isAdmin && (
          <button
            type="submit"
            className="flex w-full items-center gap-4 px-5 py-2"
            onClick={() =>
              deleteSpaceModal.show({
                space,
                onSuccess: () => {
                  redirectToRoot();
                },
              })
            }
          >
            <Icon type={deleteSpaceIcon} className="text-v2-danger" size={20} />
            <Typography.LabelSm color="text-v2-danger">
              {t("space_header.delete_space")}
            </Typography.LabelSm>
          </button>
        )}
      </>
    );
  }

  return (
    <>
      {shouldShowV3Separator && <Dropdown.Separator />}
      {shouldShowChatExport && (
        <ChatExportSpaceOptions onChatExport={onChatExport} />
      )}
      {shouldShowInviteMember && (
        <InviteMemberSpaceOptions
          space={space}
          onInviteMember={onInviteMember}
        />
      )}
      {shouldShowLeaveSpace && <LeaveSpaceOption />}
      {isAdmin && <DeleteSpaceOption />}
    </>
  );
};
